/**
 * Ascendant (Lagna) tropical ecliptic longitude.
 *
 * Standard formula (see e.g. Wikipedia "Ascendant (astrology)", "Astrology
 * and astronomy" section; originally Meeus-style spherical-astronomy
 * derivation):
 *
 *   tan(Asc) = -cos(θ) / (sin(ε)·tan(φ) + cos(ε)·sin(θ))
 *
 * where θ = local sidereal time (degrees), ε = obliquity of the ecliptic,
 * φ = observer's geographic latitude. The quadrant-correction rules after
 * atan2 pick the easterly (rising) intersection rather than its antipodal
 * (setting) point.
 *
 * Verified in docs/reference-calculator.md's real-world case (15.05.1990,
 * 14:30 Berlin) against the commercial reference calculator's published
 * HouseCusp1/Lg_Full_Degrees value (145.0557°): this implementation
 * (combined with lib/ephemeris/ayanamsha.ts for the sidereal shift)
 * reproduces it to within 0.003° — see validate-ephemeris.mjs at the repo
 * root, used for that one-time verification.
 */

function degToRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

function radToDeg(rad: number): number {
  return (rad * 180) / Math.PI;
}

function normalize360(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/**
 * @param localSiderealTimeDeg Local sidereal time in degrees (GAST hours * 15 + geographic longitude, east-positive).
 * @param latitudeDeg Observer's geographic latitude (north-positive).
 * @param obliquityDeg Obliquity of the ecliptic (degrees) at the time of birth.
 * @returns Tropical ecliptic longitude of the Ascendant, 0-360°.
 */
export function ascendantTropicalLongitude(
  localSiderealTimeDeg: number,
  latitudeDeg: number,
  obliquityDeg: number
): number {
  const theta = degToRad(normalize360(localSiderealTimeDeg));
  const eps = degToRad(obliquityDeg);
  const phi = degToRad(latitudeDeg);

  const y = -Math.cos(theta);
  const x = Math.sin(eps) * Math.tan(phi) + Math.cos(eps) * Math.sin(theta);
  let asc = radToDeg(Math.atan2(y, x));
  asc = normalize360(asc);

  // Pick the easterly (rising) solution rather than its 180°-opposite.
  if (asc < 180) asc += 180;
  else asc -= 180;

  return normalize360(asc);
}
